import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useDirty } from '../contexts/DirtyContext'
import { useTheme, type ThemePreference } from '../contexts/ThemeContext'
import { BrandLogo, ThemeIcon } from './BrandAssets'

const themeOptions: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: '淺色' },
  { value: 'dark', label: '深色' },
  { value: 'system', label: '隨系統' },
]

export function Layout() {
  const { user, loading, logout } = useAuth()
  const { dirty, setDirty } = useDirty()
  const { preference, resolvedTheme, setPreference } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [themeMenuOpen, setThemeMenuOpen] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const themeControlRef = useRef<HTMLDivElement>(null)
  const accountControlRef = useRef<HTMLDivElement>(null)
  const themeOptionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const navigate = useNavigate()
  const initials = useMemo(() => {
    if (!user) return ''
    return user.displayName.trim().slice(0, 2).toUpperCase() || 'IS'
  }, [user])

  useEffect(() => {
    if (!themeMenuOpen) return
    const selectedIndex = themeOptions.findIndex((option) => option.value === preference)
    themeOptionRefs.current[selectedIndex]?.focus()
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!themeControlRef.current?.contains(event.target as Node)) setThemeMenuOpen(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setThemeMenuOpen(false)
      themeControlRef.current?.querySelector<HTMLButtonElement>('.theme-button')?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [preference, themeMenuOpen])

  useEffect(() => {
    if (!accountMenuOpen) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!accountControlRef.current?.contains(event.target as Node)) setAccountMenuOpen(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setAccountMenuOpen(false)
      accountControlRef.current?.querySelector<HTMLButtonElement>('.account-button')?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [accountMenuOpen])

  const guardNavigation = (event: MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest('a')
    if (!dirty || !anchor || anchor.target === '_blank' || event.metaKey || event.ctrlKey) return
    const target = new URL(anchor.href, window.location.href)
    if (target.origin !== window.location.origin || target.pathname === window.location.pathname) return
    event.preventDefault()
    if (window.confirm('你有尚未儲存的變更。確定要離開嗎？')) {
      setDirty(false)
      navigate(`${target.pathname}${target.search}${target.hash}`)
    }
  }

  const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav-link nav-link--active' : 'nav-link')
  const handleThemeKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = themeOptions.length - 1
    let nextIndex: number | null = null
    if (event.key === 'ArrowDown') nextIndex = index === lastIndex ? 0 : index + 1
    if (event.key === 'ArrowUp') nextIndex = index === 0 ? lastIndex : index - 1
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = lastIndex
    if (nextIndex === null) return
    event.preventDefault()
    themeOptionRefs.current[nextIndex]?.focus()
  }

  return (
    <div className="app-shell" onClickCapture={guardNavigation}>
      <a className="skip-link" href="#main-content">
        跳到主要內容
      </a>
      <header className="site-header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label="iLoveSing 首頁">
            <BrandLogo />
          </Link>
          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => {
              setThemeMenuOpen(false)
              setAccountMenuOpen(false)
              setMenuOpen((value) => !value)
            }}
          >
            選單
          </button>
          <nav id="primary-navigation" className={menuOpen ? 'primary-nav primary-nav--open' : 'primary-nav'} aria-label="主要導覽">
            <NavLink className={navClass} to="/explore" onClick={() => setMenuOpen(false)}>
              探索
            </NavLink>
            {user && (
              <NavLink className={navClass} to="/projects" onClick={() => setMenuOpen(false)}>
                我的作品
              </NavLink>
            )}
            {user && (
              <NavLink className={navClass} to="/library" onClick={() => setMenuOpen(false)}>
                我的影片
              </NavLink>
            )}
            <NavLink className={navClass} to="/support" onClick={() => setMenuOpen(false)}>
              支持我們
            </NavLink>
            <div className="theme-control" ref={themeControlRef}>
              <button
                className="theme-button"
                type="button"
                aria-label="外觀設定"
                aria-haspopup="menu"
                aria-expanded={themeMenuOpen}
                aria-controls="theme-menu"
                title={`外觀設定，目前為${themeOptions.find((option) => option.value === preference)?.label}`}
                onClick={() => {
                  setAccountMenuOpen(false)
                  setThemeMenuOpen((value) => !value)
                }}
              >
                <ThemeIcon className="theme-icon" theme={resolvedTheme} />
              </button>
              {themeMenuOpen && (
                <div className="theme-menu" id="theme-menu" role="menu" aria-label="外觀設定">
                  {themeOptions.map((option, index) => (
                    <button
                      className="theme-option"
                      type="button"
                      role="menuitemradio"
                      aria-checked={preference === option.value}
                      tabIndex={preference === option.value ? 0 : -1}
                      ref={(element) => {
                        themeOptionRefs.current[index] = element
                      }}
                      onKeyDown={(event) => handleThemeKeyDown(event, index)}
                      onClick={() => {
                        setPreference(option.value)
                        setThemeMenuOpen(false)
                      }}
                      key={option.value}
                    >
                      <ThemeIcon className="theme-icon theme-option__glyph" theme={option.value} />
                      <span>{option.label}</span>
                      <span className="theme-option__current">
                        {preference === option.value ? '目前使用' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!loading &&
              (user ? (
                <div className="account-control" ref={accountControlRef}>
                  <button
                    className="account-button"
                    type="button"
                    aria-label={`${user.displayName} 帳號選單`}
                    aria-haspopup="menu"
                    aria-expanded={accountMenuOpen}
                    onClick={() => {
                      setThemeMenuOpen(false)
                      setAccountMenuOpen((value) => !value)
                    }}
                  >
                    {user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : <span aria-hidden="true">{initials}</span>}
                  </button>
                  {accountMenuOpen && (
                    <div className="account-menu" role="menu">
                      <div className="account-menu__identity">
                        <strong>{user.displayName}</strong>
                        {user.email && <span>{user.email}</span>}
                      </div>
                      <Link role="menuitem" to="/policies" onClick={() => setAccountMenuOpen(false)}>規則與隱私</Link>
                      {user.role === 'admin' && (
                        <Link role="menuitem" to="/admin" onClick={() => setAccountMenuOpen(false)}>管理</Link>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          if (dirty && !window.confirm('你有尚未儲存的變更。確定要登出嗎？')) return
                          setDirty(false)
                          setAccountMenuOpen(false)
                          void logout().then(() => navigate('/'))
                        }}
                      >
                        登出
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link className="button button--primary button--small" to="/login" onClick={() => setMenuOpen(false)}>
                  登入
                </Link>
              ))}
          </nav>
        </div>
      </header>
      <main id="main-content">
        <Outlet />
      </main>
      <footer className="site-footer">
        <div>
          <Link className="footer-brand" to="/" aria-label="iLoveSing 首頁">
            <BrandLogo compact />
          </Link>
          <p>把線性影片變成能跳段與預選下一段的排練路徑。</p>
        </div>
        <div className="footer-links">
          <Link to="/policies#privacy">隱私權</Link>
          <Link to="/policies#community">社群規則</Link>
          <Link to="/support">支持我們</Link>
        </div>
      </footer>
    </div>
  )
}
